// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
// Models
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { isStateFailure } from "@ms/planner-stores/lib/local/utilities/StatusUtilities";
// Utilities
import { extractPlanRoutingDetailsFromSubRoute } from "@ms/uno-routing";
/**
 * Calculates the render state for plan page
 * @param storeLoader store loader
 * @param configProvider config provider
 */ export function calculatePlanPageRenderState(storeLoader, configProvider) {
    const config = configProvider();
    const [routeStore, taskStore, planStore, bucketStore, mruStore, userStore, tenantStore, capabilitiesStore, viewStore, rosterStore, modernGroupsStore, teamsChannelStore, customFieldDefinitionStore, dependencyStore] = storeLoader.loadAll([
        Store.Route,
        Store.Tasks,
        Store.Plan,
        Store.Bucket,
        Store.Mru,
        Store.User,
        Store.Tenant,
        Store.Capabilities,
        Store.View,
        Store.Roster,
        Store.ModernGroups,
        Store.TeamsChannel,
        Store.CustomFieldDefinition,
        Store.Dependency
    ]);
    const planRoutingDetails = extractPlanRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
    if (!planRoutingDetails) {
        return ViewRenderState.None;
    }
    const currentPivot = planRoutingDetails.viewType || ViewType.Board;
    const planId = planRoutingDetails.planId;
    const planStoreReadResult = planStore.getPlan(planId);
    const planState = planStoreReadResult.status.state;
    // Terminal plan failures render a complete error surface without requiring related entity data.
    if (isStateFailure(planState)) {
        return ViewRenderState.All;
    }
    // Get critical data from stores
    const storeReadResult = taskStore.getTasksForPlan(planId);
    let filteredTasksLength = 0;
    const viewId = {
        name: ViewName.PlanPage,
        partitionKey: planId
    };
    if (currentPivot === ViewType.Board) {
        const currentTaskGrouping = viewStore.getCurrentTaskGroupingState(viewId);
        const boardViewData = taskStore.getTasksForBoardView(viewId, currentTaskGrouping);
        if (boardViewData) {
            filteredTasksLength = Object.keys(boardViewData).reduce((totalLength, key)=>{
                const tasks = boardViewData[key];
                return totalLength + (tasks?.length ?? 0);
            }, 0);
        }
    } else if (currentPivot === ViewType.Grid) {
        filteredTasksLength = taskStore.getTasksForGridView(viewId).flattenedTasks.length ?? 0;
    } else {
        filteredTasksLength = storeReadResult?.data?.length ?? 0;
    }
    const isDataFinalized = storeReadResult ? storeReadResult.status.state !== StoreEntityState.Absent : false;
    const isTasksCriticalDataLoaded = filteredTasksLength >= config.settings.performanceSettings.minimumTasksForVisualReadiness || isDataFinalized;
    const currentPlan = planStoreReadResult?.data;
    const isPlanDataLoaded = planStoreReadResult ? planStoreReadResult.status.state !== StoreEntityState.Absent : false;
    const isToDoPlan = currentPlan?.container?.containerType === ContainerType.ToDo;
    const planType = currentPlan?.getPlanType();
    const shouldWaitForDependencies = config.flights.EnableDependencies && (currentPivot === ViewType.Grid || currentPivot === ViewType.Timeline) && (planType === PlanType.Basic || planType === PlanType.Unified);
    const isDependencyDataFetched = !shouldWaitForDependencies || dependencyStore.getDependenciesForPlan(planId).status.state !== StoreEntityState.Absent;
    const bucketForPlan = bucketStore.getBucketsForPlan(planId);
    const isBucketDataLoaded = isToDoPlan || bucketForPlan.status.state !== StoreEntityState.Absent;
    // Get non-critical data from stores
    const pinnedItems = mruStore.getPinnedItems();
    const isMruFetched = pinnedItems ? pinnedItems.status.state !== StoreEntityState.Absent : false;
    let isUserStoreDataFetched = true;
    if (currentPlan?.container) {
        const { containerType, containerId } = currentPlan.container;
        switch(containerType){
            case ContainerType.DriveItem:
                {
                    if (storeReadResult?.data) {
                        const uniqueUserIds = new Set();
                        storeReadResult.data.forEach((task)=>{
                            task.userAssignments.forEach((assignment)=>{
                                uniqueUserIds.add(assignment.id);
                            });
                        });
                        isUserStoreDataFetched = Array.from(uniqueUserIds).every((id)=>userStore.getUserById(id).status.state !== StoreEntityState.Absent);
                    }
                    break;
                }
            case ContainerType.Group:
                isUserStoreDataFetched = modernGroupsStore.getModernGroup(containerId).status.state !== StoreEntityState.Absent && modernGroupsStore.getModernGroupMembers(containerId).status.state !== StoreEntityState.Absent && modernGroupsStore.getModernGroupSiteUrlAndDriveId(containerId).status.state !== StoreEntityState.Absent;
                break;
            case ContainerType.Roster:
                isUserStoreDataFetched = rosterStore.getRoster(containerId).status.state !== StoreEntityState.Absent && rosterStore.getRosterMembers(containerId).status.state !== StoreEntityState.Absent;
                break;
            case ContainerType.TeamsChannel:
                isUserStoreDataFetched = config.flights.EnablePlanInTeamsChannel ? teamsChannelStore.getTeamsChannelMembers(containerId).status.state !== StoreEntityState.Absent : true;
                break;
            default:
                isUserStoreDataFetched = true;
                break;
        }
    }
    const isTenantStoreDataFetched = tenantStore.getCurrentTenantGroupSettings()?.status.state !== StoreEntityState.Absent;
    const isCapabilitiesStoreDataFetched = capabilitiesStore.getIsPlannerPremiumEnabled()?.status.state !== StoreEntityState.Absent;
    let isCfdFetched = true;
    if (config.flights.EnableCustomFields && !isToDoPlan) {
        isCfdFetched = customFieldDefinitionStore.getCustomFieldDefinitionsForPlan(planId).status.state !== StoreEntityState.Absent;
    }
    if (isMruFetched && isUserStoreDataFetched && isTenantStoreDataFetched && isPlanDataLoaded && isBucketDataLoaded && isCapabilitiesStoreDataFetched && isCfdFetched && isDependencyDataFetched && isDataFinalized) {
        return ViewRenderState.All;
    }
    if (isPlanDataLoaded && isBucketDataLoaded && isTasksCriticalDataLoaded && isCfdFetched && isDependencyDataFetched) {
        return ViewRenderState.Critical;
    }
    return ViewRenderState.None;
}
